// @flow

import React, { Fragment } from 'react';
import { Translate } from 'react-redux-i18n';
import { connect } from 'react-redux';
import moment from 'moment';
import type { BusinessPlanBean, StoreGemini } from '../../../../types/geminiType';
import businessPlansApi from '../../../../network/api/businessPlansApi';
import { intRange } from '../../../../utils/utils';
import { getCurrentYear, months } from '../../../../utils/dateUtils';
import { getCurrentPcId } from '../../../../utils/urlUtils';
import { BUSINESS_PLAN_COLORS, BUSINESS_PLAN_COLORS_BEM } from '../../../../const';
import EllipsisLoader from '../../../../components/EllipsisLoader';

type Props = {
  changeBusinessPlanAttributes: Function,
  selectedStore: StoreGemini,
  onSelectBusinessPlan: Function,
  selectedBusinessPlanLease: ?string,
}

type State = {
  businessPlans: { [year: number]: BusinessPlanBean[] },
  yearsAvailable: number[],
  currentYear: number,
  isLoading: boolean,
}

type BusinessPlanTimelineBean = {
  lease: string,
  text: string,
  monthNumber: number,
  color: string,
  onSelect: () => void,
}

class BusinessPlanTimeline extends React.Component<Props, State> {
  state: State = {
    businessPlans: {},
    yearsAvailable: [],
    currentYear: 0,
    isLoading: true,
  };

  componentDidMount() {
    businessPlansApi.fetchAllByPcId(getCurrentPcId())
      .then(response => response.json())
      .then((businessPlans) => {
        const businessPlansMap = businessPlans.reduce((acc, obj) => {
          if (!acc[obj.year]) {
            acc[obj.year] = [{
              ...obj,
            }];
          } else {
            acc[obj.year].push(obj);
          }
          return acc;
        }, {});

        const allYears = Object.keys(businessPlansMap).map(year => parseInt(year, 10));
        const yearsAvailable = intRange(Math.min(...allYears), Math.max(...allYears));

        this.setState({
          businessPlans: businessPlansMap,
          currentYear: this.getNearestKeyFromYear(yearsAvailable),
          yearsAvailable,
          isLoading: false,
        }, this.updateMapColor);
      }).catch(() => this.setState({ isLoading: false }));
  }

  getNearestKeyFromYear = (yearsAvailable) => {
    const currentYear = getCurrentYear();
    if (yearsAvailable.includes(currentYear)) {
      return currentYear;
    }
    return yearsAvailable.reduce((acc, curr) => (
      Math.abs(curr - currentYear) < Math.abs(acc - currentYear) ? curr : acc
    ), yearsAvailable[0]);
  };

  updateYear = (reverse: boolean) => {
    const currentIndex = this.state.yearsAvailable.indexOf(this.state.currentYear);
    const multiplier = reverse ? -1 : 1;
    if ((currentIndex + multiplier) < 0
      || (currentIndex + multiplier) > this.state.yearsAvailable.length - 1
    ) {
      return;
    }
    this.setNewYear(this.state.yearsAvailable[currentIndex + multiplier]);
  };

  setNewYear = (newYear: number) => {
    this.props.onSelectBusinessPlan(null);
    this.setState({
      currentYear: newYear,
    }, this.updateMapColor);
  };

  updateMapColor = () => {
    if (Object.keys(this.state.businessPlans).length < 1) {
      return;
    }
    const currentYearBusinessPlans = this.state.businessPlans[this.state.currentYear];
    const updateColorObject = (currentYearBusinessPlans)
      ? currentYearBusinessPlans.map(businessPlan => ({
        codstr: businessPlan.codstr,
        color: BUSINESS_PLAN_COLORS[businessPlan.operation_type_description],
      }))
      : [];

    this.props.changeBusinessPlanAttributes(updateColorObject);
  };

  getBusinessPlanTimelineBeans = (): BusinessPlanTimelineBean[] => {
    if (this.props.selectedStore && this.state.businessPlans[this.state.currentYear]) {
      return this.state.businessPlans[this.state.currentYear]
        .sort((businessPlan, businessPlan2) =>
          moment(businessPlan.effective_date).isAfter(moment(businessPlan2.effective_date)))
        .filter(businessPlan => businessPlan.codstr === this.props.selectedStore.codstr)
        .map((businessPlan: BusinessPlanBean) =>
          ({
            lease: businessPlan.lease,
            text: `${businessPlan.operation_type_description.toUpperCase()}
              ${moment(businessPlan.effective_date).format('LL')}`,
            monthNumber: moment(businessPlan.effective_date).month(),
            color: (this.props.selectedBusinessPlanLease === businessPlan.lease)
              ? BUSINESS_PLAN_COLORS_BEM.selected
              : BUSINESS_PLAN_COLORS_BEM[businessPlan.operation_type_description],
            onSelect: () => this.props.onSelectBusinessPlan(businessPlan),
          }));
    }
    return [];
  }

  getArrowColor = (monthNumber: number, bpTimelineBeans: BusinessPlanTimelineBean[]): string => {
    const bpOnMonth = bpTimelineBeans.filter((bpBean: BusinessPlanTimelineBean) => bpBean.monthNumber === monthNumber);
    if (bpOnMonth.length > 0) {
      return ` year-selector-arrow${bpOnMonth[0].color}`;
    }
    return '';
  }

  getBpPositionRight = (monthId): string =>
    // positionné par rapport au côté droit si dans la deuxième moitié de l'année pour s'assurer que le carré ne soit pas écrasé sur le bord gauche
    ((monthId > 5) ? `${(11 - monthId) * 7}%` : 'unset');

  getBpPositionLeft = (monthId): string =>
    // positionné par rapport au côté gauche si dans la première moitié de l'année pour s'assurer que le carré ne soit pas écrasé sur le bord gauche
    ((monthId < 6) ? `${monthId * 7}%` : 'unset');

  render() {
    if (!this.state.businessPlans
      || Object.keys(this.state.businessPlans).length < 1
      || this.state.isLoading
    ) {
      return (
        <div className="business-plan-timeline-container">
          <div className="business-plan-timeline">
            {
              this.state.isLoading
                ? (<EllipsisLoader />)
                : (<Translate value="BUSINESS_PLAN_TIMELINE.NO_DATA" />)
            }
          </div>
        </div>
      );
    }

    const bpTimelineBeans = this.getBusinessPlanTimelineBeans();
    return (
      <div
        className={`business-plan-timeline-container${this.props.selectedStore ? ' business-plan-timeline-container--large' : ''}`}
      >
        <div className="business-plan-timeline">
          <div
            className={`year-selector-container ${this.props.selectedStore ? 'year-selector-container--large' : ''}`}
          >
            <button
              onClick={() =>
                this.setNewYear(this.getNearestKeyFromYear(this.state.yearsAvailable))
              }
              className="now-button"
            ><Translate value="BUSINESS_PLAN_TIMELINE.NOW" />
            </button>
            <div className="year-selector">
              <button
                className={`${this.state.yearsAvailable.indexOf(this.state.currentYear) > 0 ? '' : 'disabled'}`}
                onClick={() => this.updateYear(true)}
              ><i className="fa fa-chevron-left" />
              </button>
              {this.state.currentYear}
              <button
                className={`${this.state.yearsAvailable.indexOf(this.state.currentYear) < this.state.yearsAvailable.length - 1 ? '' : 'disabled'}`}
                onClick={() => this.updateYear(false)}
              >
                <i className="fa fa-chevron-right" />
              </button>
            </div>
            <div />
          </div>
          {
            this.props.selectedStore && (
              <div className="timeline-container">
                <div className="all-months">
                  {
                    months.map((month: string) => (
                      <span>{month}</span>
                    ))
                  }
                </div>
                <div className="timeline">
                  <div className="selector-arrows-container">
                    {
                      months.map((month, index) => (
                        <div
                          className={`year-selector-arrow${this.getArrowColor(index, bpTimelineBeans)}`}
                        />
                        ))
                    }
                  </div>
                  <div className="timeline-entry-container">
                    {
                      bpTimelineBeans
                        .map(bpTimelineBean => (
                          <Fragment key={bpTimelineBean.lease}>
                            <div
                              className="timeline-entry"
                              role="button"
                              onClick={bpTimelineBean.onSelect}
                            >
                              <div
                                className={`carret description carret${bpTimelineBean.color}`}
                                style={{
                                  right: this.getBpPositionRight(bpTimelineBean.monthNumber),
                                  left: this.getBpPositionLeft(bpTimelineBean.monthNumber),
                                }}
                              >
                                {bpTimelineBean.text}
                              </div>
                            </div>
                          </Fragment>
                        ))
                    }
                  </div>
                </div>
              </div>
            )}
        </div>
      </div>
    );
  }
}

export default connect(state => ({
  baseShoppingCenterList: state.shoppingCenterFilters.baseShoppingCenterList,
}), null)(BusinessPlanTimeline);
